import React, { useEffect, forwardRef, Component, RefObject, createRef, ClassAttributes, useRef } from 'react';
import { CustomObject } from '../../../types/data';
import { useTexture, useVideoTexture } from '../../../hooks';
import { BackSide, Material, Mesh } from 'three';
import { ReactThreeFiber } from 'react-three-fiber';
import { isArray, isFunction } from 'lodash';
import anime, { AnimeInstance } from 'animejs';
import HLS from 'hls.js/dist/hls.light';
import { VideoSpaceResourceId } from '../../../common/constants/xr';

const VideoSpaceContent = forwardRef<
  Mesh,
  {
    data: CustomObject;
    radius: number;
    renderOrder?: number;
    rotation?: ReactThreeFiber.Euler;
    resourceId: string;
    onLoad: () => void;
  }
>(({ data, radius, resourceId, onLoad, ...props }, ref) => {
  const { loaded: videoLoaded, texture: videoTxt, media } = useVideoTexture(resourceId);
  const { loaded: photoLoaded, texture: photoTxt } = useTexture(data.video_thumbnail_url);
  const matRef = useRef<Material>();

  useEffect(() => {
    if (!media || !data.urls?.hls) return;

    let params;

    if (HLS.isSupported()) {
      params = {
        src: data.urls.hls,
      };
    } else {
      const levels: any[] = [];
      ['1024p', '1440p', '1920p', '2048p', '2560p'].map(key => {
        const src = data.urls[key];

        if (src) {
          levels.push({
            width: Number(key.replace('p', '')),
            url: [`${src}#t=0.1`],
          });
        }
      });

      params = {
        src: `${data.urls['1920p']}#t=0.1`,
        levels,
      };
    }

    media.setResource(params);
  }, [data, media]);

  useEffect(() => {
    if (!matRef.current) return;
    matRef.current.opacity = 0;
  }, [data.id]);

  useEffect(() => {
    if (photoLoaded && isFunction(onLoad)) onLoad();
  }, [photoLoaded, onLoad]);

  return (
    <group scale={[-1, 1, 1]}>
      <mesh ref={ref} {...props}>
        <sphereBufferGeometry attach="geometry" args={[radius, 32, 32]} />
        <meshBasicMaterial attach="material" side={BackSide} transparent ref={matRef}>
          <primitive attach="map" object={videoLoaded ? videoTxt : photoTxt} />
        </meshBasicMaterial>
      </mesh>
    </group>
  );
});

type TVideoSpaceProps = ClassAttributes<{}> & {
  data: CustomObject;
  radius: number;
  rotation?: ReactThreeFiber.Euler;
  renderOrder?: number;
  enableAnimation?: boolean;
  resourceId?: string;
  onLoad?: () => void;
};

type TVideoSpaceState = {
  contentData?: {
    id: number;
    urls: { [key: string]: string };
    video_thumbnail_url: string;
  };
};

class VideoSpace extends Component<TVideoSpaceProps, TVideoSpaceState> {
  private animeInstance: AnimeInstance;
  private meshRef: RefObject<Mesh>;
  private animParams: {
    opacity: number;
  };

  constructor(props: Readonly<TVideoSpaceProps>) {
    super(props);

    this.state = {
      contentData: undefined,
    };
    this.meshRef = createRef();

    this.animParams = {
      opacity: 0,
    };
    this.animeInstance = this.createAnimInstance();
  }

  componentDidMount() {
    this.updateContentData();
  }
  componentDidUpdate() {
    this.updateContentData();
  }

  render() {
    const { radius, renderOrder, rotation, resourceId = VideoSpaceResourceId } = this.props;
    const { contentData } = this.state;

    if (contentData)
      return (
        <VideoSpaceContent
          resourceId={resourceId}
          data={contentData}
          radius={radius}
          renderOrder={renderOrder}
          rotation={rotation}
          onLoad={this.handleTextureLoad}
          ref={this.meshRef}
        />
      );

    return null;
  }

  private handleTextureLoad = () => {
    if (this.animeInstance.began && !this.animeInstance.completed) return;
    this.animReset();
    this.animPlay();
  };

  private updateContentData = () => {
    const { data } = this.props;

    if (data?.video_urls && data.id !== this.state.contentData?.id) {
      this.setState(st => ({
        ...st,
        contentData: {
          id: data.id,
          urls: data.video_urls,
          video_thumbnail_url: data.video_thumbnail_url,
        },
      }));
    }
  };

  private createAnimInstance = () => {
    const duration = 1000;

    this.animParams = {
      opacity: 0,
    };

    const animeInstance = anime({
      targets: this.animParams,
      /// animated props
      opacity: 1,
      /// end animated props
      loop: false,
      autoplay: false,
      duration,
      easing: 'linear',
      update: this.animUpdate,
      complete: this.animComplete,
    });

    return animeInstance;
  };

  private animReset = () => {
    this.animeInstance?.pause();
    this.animeInstance = this.createAnimInstance();
  };

  private animPlay = () => {
    if (this.props.enableAnimation) {
      this.animeInstance.play();
    } else {
      this.updateMaterial(1);
      this.animComplete();
    }
  };

  private updateMaterial(opacity: number) {
    if (!this.meshRef.current) return;

    const { material } = this.meshRef.current;

    if (isArray(material)) {
      material.forEach(mt => (mt.opacity = opacity));
    } else {
      material.opacity = opacity;
    }
  }

  /// animation callbacks
  private animUpdate = () => {
    const { opacity } = this.animParams;
    this.updateMaterial(opacity);
  };

  private animComplete = () => {
    const { onLoad } = this.props;

    if (isFunction(onLoad)) {
      onLoad();
    }
  };
}

export default VideoSpace;
